import type {
  FindRideResultItem,
  FindRideSearchCriteria,
  FindRideSearchResult,
} from "../types/findRide.types";

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function asString(value: unknown): string {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return null;
}

function asBoolean(value: unknown): boolean | null {
  if (typeof value === "boolean") return value;
  return null;
}

function extractRidesArray(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  const root = asRecord(payload);
  if (!root) return [];

  for (const key of ["rides", "data", "rds", "results", "rideOffers", "items"]) {
    const value = root[key];
    if (Array.isArray(value)) return value;
  }

  const data = asRecord(root.data);
  if (data) {
    for (const key of ["rides", "rds", "results", "items"]) {
      const value = data[key];
      if (Array.isArray(value)) return value;
    }
  }

  return [];
}

export function extractTotalRides(payload: unknown, ridesLength: number): number {
  const root = asRecord(payload);
  if (!root) return ridesLength;

  const direct =
    asNumber(root.totalRides) ??
    asNumber(root.TotalRides) ??
    asNumber(root.totalrides);

  if (direct != null) return Math.max(0, Math.floor(direct));

  const data = asRecord(root.data);
  if (data) {
    const nested =
      asNumber(data.totalRides) ??
      asNumber(data.TotalRides) ??
      asNumber(data.totalrides);
    if (nested != null) return Math.max(0, Math.floor(nested));
  }

  return ridesLength;
}

function firstNonEmpty(...values: unknown[]): string {
  for (const value of values) {
    const text = asString(value);
    if (text) return text;
  }
  return "";
}

/**
 * Maps a raw API ride object.
 * Uses `rideNumber` (e.g. H26B6YQF4P) as the public ride id for /ride-info links.
 * Returns null when rideNumber is missing (falls back to non-zero numeric rideId only if needed).
 */
export function normalizeRideItem(
  raw: unknown,
  index: number
): FindRideResultItem | null {
  const item = asRecord(raw) ?? {};

  const rideNumber = firstNonEmpty(item.rideNumber, item.RideNumber);
  const numericRideId = firstNonEmpty(item.rideId, item.RideId);
  const rideId =
    rideNumber ||
    (numericRideId && numericRideId !== "0" && Number(numericRideId) !== 0
      ? numericRideId
      : "");

  if (!rideId) return null;

  const fromLabel =
    firstNonEmpty(item.fromLocality, item.from) || "Pickup";
  const toLabel = firstNonEmpty(item.toLocality, item.to) || "Drop";

  const vehicleMake = firstNonEmpty(item.vehicleMake, item.make);
  const vehicleModel = firstNonEmpty(item.vehicleModel, item.model);

  return {
    key: `${rideId}-${index}`,
    rideId,
    fromLabel,
    toLabel,
    startDate: firstNonEmpty(item.startDate, item.date),
    startTime: firstNonEmpty(item.startTime, item.time),
    startTimeZone: firstNonEmpty(item.startTimeZone, item.timezone, item.tz),
    ridePrice: asNumber(item.ridePrice) ?? asNumber(item.pricePerSeat),
    priceCurrency: firstNonEmpty(item.priceCurrency, item.currency, item.cry) || "INR",
    seatsAvailability:
      asNumber(item.seatsAvailability) ?? asNumber(item.seatsAvailable),
    vehicleMake,
    vehicleModel,
    vehicleYear: firstNonEmpty(item.vehicleyear, item.vehicleYear),
    vehicleLicensePlate: firstNonEmpty(
      item.vehicleLicensePlate,
      item.licensePlate,
      item.reg
    ),
    riderName: firstNonEmpty(item.riderName, item.hostName, item.ridername),
    aboutRider: firstNonEmpty(item.aboutRider, item.bioTitle),
    noOfTimesBooked: asNumber(item.noOfTimesBooked),
    percentageOfCompanion: asNumber(item.percentageOfCompanion),
    estimatedTravelTime: firstNonEmpty(item.estimatedTravelTime),
    distanceFromUserToPickupKM: asNumber(item.distanceFromUserToPickupKM),
    timeToReachPickup: asNumber(item.timeToReachPickup),
    distanceFromDropToUserDestKM: asNumber(item.distanceFromDropToUserDestKM),
    totalRideDistanceKM: asNumber(item.totalRideDistanceKM),
    canReachOnTime: asBoolean(item.canReachOnTime),
    searchTimeGroup: firstNonEmpty(item.searchTimeGroup),
    searchTimeGroupDescription: firstNonEmpty(item.searchTimeGroupDescription),
    isDailyCommuteRide: asBoolean(item.isDailyCommuteRide) ?? false,
    raw,
  };
}

export function mapFindRideResponse(
  payload: unknown,
  _criteria: FindRideSearchCriteria
): FindRideSearchResult {
  const rides = extractRidesArray(payload)
    .map((item, index) => normalizeRideItem(item, index))
    .filter((ride): ride is FindRideResultItem => ride != null);

  const totalRides = extractTotalRides(payload, rides.length);
  return { totalRides, rides, raw: payload };
}
